import { useEffect, useState } from "react";
import Button from "../../components/Button";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import { apiRequest, formatPrice } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";

const initialForm = { name: "", email: "", password: "", phone: "" };

export default function Account() {
  const auth = useAuth();
  const toast = useToast();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState(initialForm);
  const [orders, setOrders] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!auth.customer) return;
    setLoading(true);
    Promise.all([
      apiRequest("/api/account/orders", { tokenType: "customer" }),
      apiRequest("/api/account/downloads", { tokenType: "customer" }),
    ])
      .then(([orderResponse, downloadResponse]) => {
        setOrders(orderResponse.data);
        setDownloads(downloadResponse.data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [auth.customer]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await auth.customerLogin({ email: form.email, password: form.password });
      } else {
        await auth.customerRegister(form);
      }
      setForm(initialForm);
      toast?.show("Welcome back.");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (!auth.customer) {
    return (
      <section className="section account-page">
        <div className="container auth-panel glass-card">
          <span className="eyebrow">Customer account</span>
          <h1>{mode === "login" ? "Login" : "Create Account"}</h1>
          <form className="form-card" onSubmit={handleSubmit}>
            {mode === "register" && (
              <>
                <label><span style={{textAlign:"start"}}>Name</span><input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} /></label>
                <label><span style={{textAlign:"start"}}>Phone</span><input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
              </>
            )}
            <label><span style={{textAlign:"start"}}>Email</span><input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></label>
            <label><span style={{textAlign:"start"}}>Password</span><input type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} /></label>
            {error && <p className="form-status error">{error}</p>}
            <Button type="submit" disabled={loading}>{loading ? "Please wait..." : mode === "login" ? "Login" : "Create Account"}</Button>
          </form>
          <button className="text-button" type="button" onClick={() => setMode(mode === "login" ? "register" : "login")}>
            {mode === "login" ? "Need an account?" : "Already have an account?"}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="section account-page">
      <div className="container">
        <div className="account-head">
          <div>
            <span className="eyebrow">Library</span>
            <h1>Welcome, {auth.customer.name}</h1>
          </div>
          <Button variant="secondary" onClick={auth.customerLogout}>Logout</Button>
        </div>
        {loading && <LoadingSpinner />}
        {error && <p className="form-status error">{error}</p>}
        <div className="account-grid">
          <div className="glass-card account-card">
            <h2 style={{marginBottom:"20px"}}>Downloads</h2>
            {!downloads.length ? <p>No downloads are available yet.</p> : downloads.map((item) => (
              <a className="download-row" key={item.token} href={item.url}>
                <strong>{item.product_name}</strong>
                <span>{item.order_number}</span>
              </a>
            ))}
          </div>
          <div className="glass-card account-card">
            <h2 style={{marginBottom:"20px"}}>Orders</h2>
            {!orders.length ? <p>No orders yet.</p> : orders.map((order) => (
              <div className="order-row" key={order.id}>
                <strong>{order.order_number}</strong>
                <span>{order.order_status}</span>
                <b>{formatPrice(order.total)}</b>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
